import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../stores/authStore';
import { cn } from '../../utils/utils';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isValidEmail, normalizeEmail } from '../../utils/emailValidation';

const EMAIL_INVALID_ERROR = 'Please enter a valid email address';

const LoginForm = () => {
  const navigate = useNavigate();
  const snap = useSnapshot(authStore);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const clearError = () => {
    if (snap.error) authStore.error = null;
    setSuccessMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!email.trim()) {
      authStore.error = 'Please enter your email';
      return;
    }
    if (!isValidEmail(email)) {
      authStore.error = EMAIL_INVALID_ERROR;
      return;
    }

    const normalizedEmail = normalizeEmail(email);
    setEmail(normalizedEmail);

    try {
      await authStore.login(normalizedEmail, password);
      setSuccessMsg('Login successful! Redirecting...');
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      console.error('Login failed:', error);
      // Error is already handled by authStore
    }
  };

  const handleEmailChange = (e)=> {
    setEmail(e.target.value);
    if (snap.error) clearError(); // Only clear error if one exists
  };

  const handlePasswordChange = (e)=> {
    setPassword(e.target.value);
    if (snap.error) clearError(); // Only clear error if one exists
  };

  const handleRememberMeChange = (e)=> {
    setRememberMe(e.target.checked);
  };

  const handleSignUpClick = ()=> {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('switch-auth', { detail: { to: 'signup' } }));
    }
  };

  return (
    <div className='w-full py-4 font-segoe'>
      <h2 className="font-helvetica text-4xl font-semibold mb-2 text-white mt-6">
        Welcome back!
      </h2>
      <p className="text-white/80 text-sm mb-6">
        Please login to access the E-learning portal.
      </p>

      {/* Error Message */}
      {snap.error && (
        <div className="flex items-center gap-2 bg-red-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-light mb-6 w-fit opacity-90 transition-opacity duration-500 ease-in-out">
          <AlertTriangle size={16} />
          {snap.error}
        </div>
      )}

      {/* Success Message */}
      {successMsg && (
        <div className="flex items-center gap-2 bg-green-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-light mb-6 w-fit opacity-75">
          <CheckCircle2 size={16} />
          {successMsg}
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4 mt-8">
        <div className='md:mb-6'>
          <label htmlFor="email" className="block text-md text-white/90 mb-2">
            EMAIL
          </label>
          <input
            id="email"
            type="email"
            className="w-full px-4 py-4 glass rounded-3xl border border-white/15 placeholder-white/75 focus:border-[var(--primary-font)] transition-all outline-none text-sm"
            placeholder="your.email@gmail.com"
            value={email}
            onChange={handleEmailChange}
            required
            autoComplete="email"
          />
        </div>

        <div className='md:mb-6'>
          <label htmlFor="password" className="block text-md text-white/90 mb-2">
            PASSWORD
          </label>
          <input
            id="password"
            type="password"
            className="w-full px-4 py-4 glass rounded-3xl border border-white/15 placeholder-white/75 focus:border-[var(--primary-font)] transition-all outline-none text-sm"
            placeholder="Enter your password"
            value={password}
            onChange={handlePasswordChange}
            required
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center justify-end text-md">
          <button
            type="button"
            className="text-white/90 underline font-semibold font-segoe hover:text-white hover:underline bg-transparent border-none cursor-pointer"
            onClick={(e) => {
              navigate('/forgot-password');
            }}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          className={cn(
            'w-full py-3 rounded-lg font-semibold text-md bg-white text-gray-900/70 hover:bg-white/90 transition-all duration-200 mt-6',
            'flex items-center justify-center gap-2',
            snap.loading ? 'opacity-80 cursor-not-allowed' : 'hover:shadow-lg hover:scale-[1.02]'
          )}
          disabled={snap.loading}
        >
          {snap.loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Logging in...</span>
            </>
          ) : (
            'Login'
          )}
        </button>
      </form>

      {/* Register Link */}
      <div className="mt-6 text-center text-sm text-white/80">
        Don't have an account?{' '}
        <button
          type="button"
          className="text-white font-semibold hover:underline cursor-pointer transition-all ml-1 bg-transparent border-none"
          onClick={handleSignUpClick}
        >
          Sign up
        </button>
      </div>
    </div>
  );
};

export default LoginForm;